import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useState } from "react";

import { useViewport } from "@/contexts/ViewportContext";
import { useAppNavigation } from "@/hooks/application/navigation";
import { useLogin } from "@/hooks/domain/auth";
import { ApiError } from "@/interfaces/common/Error";
import { AuthNavigation } from "@/navigation/stacks/AuthenticationStack";
import {
  requestRecoveryPassword,
  updatePassword,
  validateRecoveryPassCode,
} from "@/services/api/user";

import RecoveryPasswordMobile from "./RecoveryPassword.mobile";
import RecoveryPasswordWeb from "./RecoveryPassword.web";
import { CodeFormData, EmailFormData } from "./schema";

const STEPS = 3;

const RecoveryPassword = () => {
  const { isWebMode } = useViewport();
  const { navigate } = useAppNavigation<AuthNavigation>();
  const { mutate: login } = useLogin();

  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<EmailFormData & CodeFormData>({} as any);
  const [errors, setErrors] = useState({
    email: null,
    code: null,
    password: null,
  });

  const onNext = () => {
    if (currentStep < STEPS) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const onPrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
    if (currentStep === 0) {
      navigate("login");
    }
  };

  const { mutate: onSubmitEmail, isLoading: isSubmitingEmail } = useMutation({
    mutationFn: requestRecoveryPassword,
    onSuccess: (_, variables) => {
      setForm((prev) => ({ ...prev, email: variables }));
      onNext();
    },
    onError: (error: AxiosError<ApiError>) => {
      setErrors((prev) => ({ ...prev, email: error.response?.data?.error }));
    },
  });

  const { mutate: onSubmitCode, isLoading: isSubmitingCode } = useMutation({
    mutationFn: validateRecoveryPassCode,
    onSuccess: (_, variables) => {
      setForm((prev) => ({ ...prev, code: variables }));
      onNext();
    },
    onError: (error: AxiosError<ApiError>) => {
      setErrors((prev) => ({ ...prev, code: error.response?.data?.error }));
    },
  });

  const { mutate: onSubmitPassword, isLoading: isSubmitingPassword } = useMutation({
    mutationFn: updatePassword,
    onSuccess: (_, variables) => {
      login({
        email: form.email,
        password: variables.password,
      });
    },
    onError: (error: AxiosError<ApiError>) => {
      setErrors((prev) => ({ ...prev, password: error.response?.data?.error }));
    },
  });

  const Screen = isWebMode ? RecoveryPasswordWeb : RecoveryPasswordMobile;

  return (
    <Screen
      currentStep={currentStep}
      errors={errors}
      onBack={onPrev}
      onCancel={() => navigate("login")}
      onSubmitCode={({ code }) => onSubmitCode(code)}
      onSubmitEmail={({ email }) => onSubmitEmail(email)}
      onSubmitPassword={({ password }) =>
        onSubmitPassword({
          password,
          code: form.code,
        })
      }
      onHaveCodePress={onNext}
      isSubmitingCode={isSubmitingCode}
      isSubmitingEmail={isSubmitingEmail}
      isSubmitingPassword={isSubmitingPassword}
    />
  );
};

export default RecoveryPassword;
